import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../lib/api";
import { fmtDate } from "../lib/i18n";
import type { CurveSpec, TrendPoint } from "../lib/types";

const TONE: Record<string, string> = {
  healthy: "var(--chart-band-healthy)",
  ow: "var(--chart-band-ow)",
  ob: "var(--chart-band-ob)",
  sev: "var(--chart-band-sev)",
};

/**
 * BMI-for-age chart drawn from the API's reference spec: WHO SD lines and cut-off bands by
 * default in the UAE edition (the cut-offs step at 5 years, where WHO 2006 hands over to
 * WHO 2007), or CDC percentiles for sites configured for CDC. Always laid out left-to-right,
 * also in the Arabic interface, because the age axis is numeric.
 */
export default function BmiChart({ sex, trend, ageNow }: { sex: string; trend: TrendPoint[]; ageNow: number }) {
  const [spec, setSpec] = useState<CurveSpec | null>(null);
  const [hover, setHover] = useState<TrendPoint | null>(null);
  const box = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(640);

  useEffect(() => {
    api.curves(sex).then(setSpec).catch(() => setSpec(null));
  }, [sex]);

  // The SVG scales to its container, so on a phone 11-unit labels would render ~6px high.
  // Grow text (and the margins that hold it) as the chart gets narrower.
  useEffect(() => {
    const el = box.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(([e]) => setWidth(e.contentRect.width || 640));
    ro.observe(el);
    return () => ro.disconnect();
  }, [spec]);
  const scale = Math.min(2, Math.max(1, 600 / width));
  const fs = 11 * scale;
  const touch = typeof window !== "undefined" && window.matchMedia?.("(hover: none)").matches;

  const W = 640, H = 320, R = 14, T = 12;
  // Left margin holds the rotated axis title plus tick numbers; bottom holds ticks plus the age title.
  const L = Math.round(20 + 22 * scale), B = Math.round(12 + 22 * scale);
  const view = useMemo(() => {
    if (!spec || spec.points.length === 0) return null;
    const ages = trend.map((t) => t.age_years);
    const aMin = Math.max(2, Math.floor(Math.min(ageNow, ...ages) - 2));
    const aMax = Math.min(19, Math.ceil(Math.max(ageNow, ...ages) + 1.5));
    const pts = spec.points.filter((p) => p.age_years >= aMin - 0.3 && p.age_years <= aMax + 0.3);
    const lineVals = pts.flatMap((p) => spec.lines.map((l) => p[l.key]).filter((v): v is number => v != null));
    const bmis = trend.map((t) => t.bmi);
    const yMin = Math.floor(Math.min(...lineVals, ...bmis) * 0.92);
    const yMax = Math.ceil(Math.max(...lineVals, ...bmis) + 1.5);
    const x = (a: number) => L + ((a - aMin) / (aMax - aMin)) * (W - L - R);
    const y = (b: number) => T + (1 - (Math.min(Math.max(b, yMin), yMax) - yMin) / (yMax - yMin)) * (H - T - B);
    // A band edge of null means the chart bottom/top. A named cut-off with no value at some age
    // (e.g. no 'severe' class under 5 on WHO 2006) means "not defined here": pin it to the top,
    // so the band above it collapses and the band below it runs to the top.
    const val = (p: (typeof pts)[number], k: string | null, fallback: number) =>
      k == null ? fallback : p[k] == null ? yMax : (p[k] as number);
    const line = (k: string) => {
      let d = "";
      let pen = false;
      for (const p of pts) {
        const v = p[k];
        if (v == null) { pen = false; continue; }
        d += `${pen ? "L" : "M"}${x(p.age_years).toFixed(1)},${y(v).toFixed(1)}`;
        pen = true;
      }
      return d;
    };
    const area = (lo: string | null, hi: string | null) => {
      const top = pts.map((p) => `${x(p.age_years).toFixed(1)},${y(val(p, hi, yMax)).toFixed(1)}`);
      const bot = [...pts].reverse().map((p) => `${x(p.age_years).toFixed(1)},${y(val(p, lo, yMin)).toFixed(1)}`);
      return `M${top.join("L")}L${bot.join("L")}Z`;
    };
    // Fewer ticks when labels are enlarged, so they do not collide.
    const xTicks: number[] = [];
    const xStep = (aMax - aMin) * scale > 10 ? 2 : 1;
    for (let a = Math.ceil(aMin); a <= aMax; a += xStep) xTicks.push(a);
    const yTicks: number[] = [];
    const step = (yMax - yMin) * scale > 30 ? 5 : 2;
    for (let b = Math.ceil(yMin / step) * step; b <= yMax; b += step) yTicks.push(b);
    return { x, y, line, area, xTicks, yTicks };
  }, [spec, trend, ageNow, scale, L, B]);

  if (!spec) return <div className="muted small">Loading chart…</div>;
  if (!view) return <div className="muted small">Reference curves unavailable.</div>;
  const { x, y, line, area, xTicks, yTicks } = view;

  return (
    <div className="chart" dir="ltr" ref={box}>
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`BMI-for-age chart, ${spec.reference}`}
        onClick={(e) => { if (!(e.target as Element).closest("[data-point]")) setHover(null); }}>
        {spec.bands.map((b) => <path key={b.tone} d={area(b.lower, b.upper)} fill={TONE[b.tone]} />)}
        {yTicks.map((b) => (
          <g key={b}>
            <line x1={L} x2={W - R} y1={y(b)} y2={y(b)} stroke="var(--border)" strokeWidth={0.5} />
            <text x={L - 6} y={y(b) + fs * 0.36} fontSize={fs} textAnchor="end" fill="var(--text-muted)">{b}</text>
          </g>
        ))}
        {xTicks.map((a) => (
          <text key={a} x={x(a)} y={H - B + fs * 1.45} fontSize={fs} textAnchor="middle" fill="var(--text-muted)">{a}</text>
        ))}
        <text x={(L + W - R) / 2} y={H - 4} fontSize={fs} textAnchor="middle" fill="var(--text-muted)">Age (years)</text>
        <text x={fs * 1.1} y={(T + H - B) / 2} fontSize={fs} textAnchor="middle" fill="var(--text-muted)"
          transform={`rotate(-90 ${fs * 1.1} ${(T + H - B) / 2})`}>BMI (kg/m²)</text>
        {spec.lines.map((l) => (
          <path key={l.key} d={line(l.key)} fill="none" stroke="var(--chart-line)"
            strokeWidth={l.strong ? 1.4 : 0.9} strokeDasharray={l.dashed ? "4 3" : undefined} />
        ))}
        {trend.length > 1 && (
          <path d={trend.map((t, i) => `${i ? "L" : "M"}${x(t.age_years)},${y(t.bmi)}`).join("")}
            fill="none" stroke="var(--chart-point)" strokeWidth={2} />
        )}
        {trend.map((t, i) => (
          <g key={`${t.date}-${i}`} data-point tabIndex={0} style={{ cursor: "pointer", outline: "none" }}
            // Hover only for a real mouse: after a tap, browsers fire a synthetic mouseleave that
            // would clear the values the tap just showed.
            onPointerEnter={(e) => { if (e.pointerType === "mouse") setHover(t); }}
            onPointerLeave={(e) => { if (e.pointerType === "mouse") setHover(null); }}
            onFocus={() => setHover(t)} onBlur={() => setHover(null)}
            onClick={() => setHover(t)}>
            <title>{`${fmtDate(t.date)}: BMI ${t.bmi}, z ${t.z >= 0 ? "+" : ""}${t.z} (${t.reference})`}</title>
            {/* Invisible larger target so a fingertip can hit the point. */}
            <circle cx={x(t.age_years)} cy={y(t.bmi)} r={14 * scale} fill="transparent" />
            <circle cx={x(t.age_years)} cy={y(t.bmi)} r={(hover === t ? 6 : 4.5) * Math.min(scale, 1.5)}
              fill="var(--chart-point)" stroke="var(--surface)" strokeWidth={1.5} />
          </g>
        ))}
      </svg>
      <div className="legend">
        {spec.bands.map((b) => <span key={b.tone} style={{ ["--c" as string]: TONE[b.tone] }}>{b.label}</span>)}
      </div>
      <div className="small muted" style={{ minHeight: 20, marginTop: 4 }}>
        {hover
          ? `${fmtDate(hover.date)} · age ${hover.age_years.toFixed(1)} · ${hover.height_cm ?? "?"} cm, ${hover.weight_kg ?? "?"} kg · BMI ${hover.bmi} · z ${hover.z >= 0 ? "+" : ""}${hover.z} · ${hover.reference}`
          : `${spec.reference}. Lines: ${spec.lines.map((l) => l.label).join(", ")}. ${touch ? "Tap" : "Hover over or tap"} a point for values.`}
      </div>
    </div>
  );
}
